import { useEffect, useState } from 'react';

import styled from 'styled-components';

import NoticeCard from 'components/card/NoticeCard';

import { color } from 'asserts/styles/colors';
import DefaultInput from 'components/input/DefaultInput';
import { useQuery } from '@tanstack/react-query';
import { QueryKeys, restfetcher } from 'queryClient';
import { useAtomValue } from 'jotai';
import companyInforAtom from 'atom/companyInfoAtom';
import Paginav from 'components/paginav/Pagination';
import { useNavigate } from 'react-router-dom';

interface NoticeProps {
  boardData: BoardListData[];
}

const Notice = ({ boardData }: NoticeProps) => {
  const navigate = useNavigate();

  const companyInfor = useAtomValue(companyInforAtom);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [pageNumber, setPageNumber] = useState(0);

  const params = {
    categoryidx: companyInfor.categoryidx,
    countrycode: 'KR',
    menuidx: 15,
    submenuidx: 0,
    keyword: searchInputValue,
    page: pageNumber
  };

  const boardSearchListApi = useQuery({
    queryKey: [QueryKeys.BOARD_SEARCH_NOTICE, params],
    queryFn: () =>
      restfetcher({
        method: 'POST',
        path: 'biz/board/search',
        params: params
      })
  });

  useEffect(() => {
    const urlSearchParams = new URLSearchParams();

    urlSearchParams.set('menu', '15');
    urlSearchParams.set('submenu', '');

    navigate(`${window.location.pathname}?${urlSearchParams.toString()}`, { replace: true });
  }, [navigate]);

  const { data: boardList } = boardSearchListApi;

  const boardSearchList: BoardSearchList[] = boardList && boardList.data?.list;

  const handleChangeSearchInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSearchInputValue(value);
  };

  const handleSearch = (
    searchValue: string,
    event?:
      | React.ChangeEvent<HTMLInputElement>
      | React.MouseEvent<HTMLElement, MouseEvent>
      | React.KeyboardEvent<HTMLInputElement>,
    info?: { source?: 'clear' | 'input' }
  ) => {
    if (info?.source === 'input') {
      boardSearchListApi.refetch();
    }
  };

  const handlePageChange = (page: number) => {
    setPageNumber(page);
  };

  useEffect(() => {
    if (companyInfor.categoryidx !== 0) {
      boardSearchListApi.refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardData, companyInfor.categoryidx, pageNumber]);

  return (
    <Wrapper>
      <div>
        <DefaultInput
          name="search"
          search
          placeholder="입력하세요"
          height="middle"
          onChange={handleChangeSearchInput}
          onSearch={handleSearch}
        />
      </div>
      <CardWrapper>
        {boardSearchList &&
          boardSearchList.reverse().map((item, index) => {
            const { title, attachments, boardidx, contents, submenuidx, updatedate } = item;

            return (
              <NoticeCard
                key={index}
                title={title}
                boardidx={boardidx}
                attachments={attachments}
                contents={contents}
                submenuidx={submenuidx}
                boardSearchList={boardSearchList}
                currentIndex={index}
                updatedate={updatedate}
              />
            );
          })}
      </CardWrapper>
      {boardList?.data?.totalPages > 1 && (
        <PaginationWrapper>
          <Paginav
            total={boardList.data?.totalElements}
            currentPage={pageNumber}
            pageSize={boardList.data?.size}
            onPageChange={handlePageChange}
          />
        </PaginationWrapper>
      )}
    </Wrapper>
  );
};

export default Notice;

const Layout = styled.div`
  display: flex;
  justify-content: center;
  width: 890px;
  gap: 32px;
  margin: 0 auto;
`;

const Wrapper = styled(Layout)`
  padding-top: 48px;
  flex-direction: column;
  color: ${color.navyLight};
  margin-bottom: 128px;

  & > div:first-of-type {
    width: 320px;
  }
`;

const CardWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
`;

export const PaginationWrapper = styled.div`
  margin: 48px 0 128px;
  display: flex;
  justify-content: end;
`;
